import type { FC } from 'react';
import { Box, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material';

import type { DashboardWidgetComparisonType } from 'src/constants/dashboard';
import type { DataSeriesType } from 'src/types/dashboard';
import { isNumber, numberOrDash, roundOrNull } from 'src/helper/helper';
import DoughnutChart from 'src/components/dashboard/charts/DoughnutChart';
import { useCrossSurveyBenchmark } from 'src/components/dashboard/DashboardContext/CrossSurveyBenchmarkContext';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import DoughnutSkeleton from 'src/components/dashboard/DashboardPage/widgets/DoughnutWidget/DoughnutWidgetSkeleton';
import useChatbotData from 'src/components/dashboard/DashboardPage/widgets/DoughnutWidget/useChatbotData';
import { useDoughnutReData } from 'src/components/dashboard/DashboardPage/widgets/DoughnutWidget/useDoughnutReData';
import useQuestionData from 'src/components/dashboard/hooks/useQuestionData';

import { styles } from './index.styles';

export const DOUGHNUT_WIDGET_TEST_ID = 'doughnut-widget';
export const CROSS_SURVEY_BENCHMARK_TEST_ID = 'cross-survey-benchmark';

export interface DoughnutWidgetProps {
  bigVersion: boolean;
  comparisonType: DashboardWidgetComparisonType;
  question: string;
  benchmarkQuestion?: string;
  dataseries?: { content?: DataSeriesType };
  cutoutRegularDoughnut?: number;
}

export const DoughnutWidget: FC<DoughnutWidgetProps> = ({
  bigVersion,
  comparisonType,
  question,
  benchmarkQuestion,
  dataseries,
  cutoutRegularDoughnut = 70,
}) => {
  const theme = useTheme();
  const { loading, errorMsg, refetch, data } = useStatistics();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const showBigWidget = bigVersion || !upLg;
  const { chartData, comparisonDataName, comparisonAggregate, focalAggregate } =
    useQuestionData(comparisonType, question, data);
  const diagram = (dataseries?.content as DataSeriesType)?.diagram?.[0];

  const { loading: benchmarkLoading, data: benchmarkData } =
    useCrossSurveyBenchmark();
  const cutoutRegularDoughnutWithBenchmark = isNumber(cutoutRegularDoughnut)
    ? 2 * cutoutRegularDoughnut - 100
    : 40;

  const {
    totalValue,
    yoyDiffValue,
    comparisonValue,
    showPercent,
    benchmarkResultForWidget,
    benchmarkValue,
    benchmarkColor,
    benchmarkDataSet,
  } = useDoughnutReData({
    diagram,
    focalAggregate,
    comparisonAggregate,
    benchmarkData,
    benchmarkQuestion,
    cutoutRegularDoughnut,
  });

  useChatbotData(comparisonAggregate, focalAggregate);

  if (loading || errorMsg) {
    return (
      <DoughnutSkeleton
        bigVersion={bigVersion}
        errorMsg={errorMsg}
        refetch={refetch}
      />
    );
  }

  if (!focalAggregate) return null;

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={styles.wrapper}
      data-test-id={DOUGHNUT_WIDGET_TEST_ID}
    >
      <Stack sx={styles.headlineWrapper}>
        <Box sx={styles.headline}>{focalAggregate.label}</Box>
        {!benchmarkLoading && benchmarkResultForWidget ? (
          // cross survey benchmark
          <Stack
            direction={'row'}
            data-test-id={CROSS_SURVEY_BENCHMARK_TEST_ID}
          >
            <Box sx={styles.squareElement(benchmarkColor)}></Box>
            <Box sx={styles.subline}>
              <Stack direction={'row'} spacing={0.5}>
                <Box>{benchmarkResultForWidget.label ?? '--'}</Box>
                <Box>{numberOrDash(benchmarkValue, showPercent)}</Box>
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </Stack>

      <Stack direction="column" spacing={1}>
        <Stack direction="row" sx={{ justifyContent: 'center' }}>
          <DoughnutChart
            data={chartData}
            percentageValue={numberOrDash(totalValue, showPercent)}
            bigVersion={showBigWidget}
            diffValue={yoyDiffValue}
            benchmarkDataSet={benchmarkDataSet}
            innerRadius={
              benchmarkDataSet
                ? cutoutRegularDoughnutWithBenchmark
                : cutoutRegularDoughnut
            }
            showPercentageDefault={false}
          />
        </Stack>

        {comparisonDataName && (
          // benchmark within same survey
          <Tooltip arrow placement="bottom" title={comparisonDataName}>
            <Stack direction="row" spacing={0.5} sx={styles.label}>
              <Box sx={styles.labelText}>{comparisonDataName}</Box>
              <Box sx={styles.percentage}>
                {numberOrDash(roundOrNull(comparisonValue), showPercent)}
              </Box>
            </Stack>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export default DoughnutWidget;
